import React, { FC } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import LaneArrowIcon from 'transfix-icons/build/LaneArrowIcon'
import Card from 'transfix-ui/components/Card'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { useQueryParams, BooleanParam } from 'use-query-params'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { DedicatedLane } from 'apps/fleet-planner/types/apiTypes'
import { shouldShowExpirationTag } from 'apps/fleet-planner/utils/dedicatedLanesHelpers'
import formatCurrency from 'utils/formatCurrency'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { DedicatedLanesExpiringBadge } from './DedicatedLanesExpiringBadge'

const useDedicatedLaneStyles = makeStyles(() => ({
  text: {
    color: brandColors.coolGray5,
  },
  divider: {
    border: `1px solid ${brandColors.coolGray2}`,
  },
}))

export interface IDedicatedLanesRowMobileProps {
  dedicatedLane: DedicatedLane
}

export const DedicatedLanesContractRowMobile: FC<IDedicatedLanesRowMobileProps> = ({
  dedicatedLane,
}) => {
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isDedicatedLanesPageCapacityEnabled } = getFlag('dedicatedLanesPageCapacity')
  const { enabled: isDedicatedLanesExpiringSoonTagEnabled } = getFlag(
    'dedicatedLanesExpiringSoonTag'
  )
  const { trackEvent } = useFMSAnalytics()
  const classes = useDedicatedLaneStyles()
  const history = useHistory()
  const {
    capacityPerWeek = '0',
    carrier,
    deliveryCity,
    deliveryState,
    equipment,
    expirationDate,
    // ID is missing in resp
    id,
    isOnlyDropTrailer,
    pickupCity,
    pickupState,
    rate,
    rateType,
  } = dedicatedLane || {}
  const { name: carrierName } = carrier || {}
  const expirationMessage = expirationDate ? 'Ends ' : 'End date: Never'
  const loadingMethodMessage = isOnlyDropTrailer ? 'Drop-Drop' : 'Live-Live'
  const rateMessage = rate ? formatCurrency(rate) : 'Rate N/A'
  const rateTypeMessage = rateType === 'flat' ? 'Linehaul' : 'RPM'
  const [{ mobileApp }] = useQueryParams({ mobileApp: BooleanParam })
  const mobileAppQueryParam = mobileApp ? '?mobileApp=1' : ''

  const clickRowHandler = () => {
    trackEvent('Dedicated Lanes Page', 'VIEWED_DEDICATED_LANES_DETAILS_MODAL')
    history.push(`/dedicated-lanes/contracts/${id}${mobileAppQueryParam}`)
  }
  const showExpirationTag = shouldShowExpirationTag(dedicatedLane)

  return (
    <Box onClick={clickRowHandler}>
      <Card noPadding>
        <Box p={1.5}>
          {dedicatedLane && showExpirationTag && isDedicatedLanesExpiringSoonTagEnabled && (
            <Box>
              <DedicatedLanesExpiringBadge marginBottom='16px' />
            </Box>
          )}
          <Typography className={classes.text} variant='caption'>
            {carrierName}
          </Typography>
          <Box display='flex' alignItems='center'>
            <Box pr={1}>
              <Typography variant='subtitle1'>{`${pickupCity}, ${pickupState}`}</Typography>
            </Box>
            <LaneArrowIcon size='large' />
            <Box pl={1}>
              <Typography variant='subtitle1'>{`${deliveryCity}, ${deliveryState}`}</Typography>
            </Box>
          </Box>
          <Typography className={classes.text} variant='caption'>
            {`${equipment} · ${loadingMethodMessage} ${
              expirationDate ? `· ${expirationMessage}` : ''
            } `}
            {expirationDate && (
              <FormattedDateTime
                inSentence
                datetime={expirationDate}
                displayTimezone={moment.tz.guess() as AllowedTimezones}
                format='dateAndYear'
              />
            )}
          </Typography>
          <Box pt={1.5} pb={2}>
            <div className={classes.divider} />
          </Box>
          <Box display='flex'>
            <div>
              <Typography variant='subtitle1'>{rateMessage}</Typography>
            </div>
            {isDedicatedLanesPageCapacityEnabled && (
              <Box marginLeft='auto'>
                <Typography variant='subtitle1'>Up to {capacityPerWeek} loads</Typography>
              </Box>
            )}
          </Box>
          <Box display='flex'>
            <div>
              <Typography variant='caption' className={classes.text}>
                {rateTypeMessage}
              </Typography>
            </div>
            <Box marginLeft='auto'>
              <Typography variant='caption' className={classes.text}>
                Per week
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  )
}
